import React, { useEffect, useState } from "react";
import {
  LocalizationMap,
  ScrollMode,
  Viewer,
  Worker,
} from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import de_DE from "@react-pdf-viewer/locales/lib/de_DE.json";
import { X, AlertCircle, Download } from "@geist-ui/icons";
import { useTheme } from "@geist-ui/core";
import { motion, AnimatePresence as AnimatePresenceOrig } from "framer-motion";
import { useAuth0 } from "@auth0/auth0-react";
import { watermarkPdf } from "../../../../utility/watermark-pdf";

const AnimatePresence = AnimatePresenceOrig as any;

interface PdfViewerGeistModalProps {
  url: string | null;
  name: string | null;
  fileName?: string | null;
  isOpen: boolean;
  onClose: () => void;
  watermark?: boolean;
}

export function PdfViewerGeistModal({
  url,
  name,
  fileName,
  isOpen,
  onClose,
  watermark = false,
}: PdfViewerGeistModalProps) {
  const theme = useTheme();
  const { user } = useAuth0();

  const [watermarkedData, setWatermarkedData] = useState<Uint8Array | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !url || !watermark) {
      setWatermarkedData(null);
      setError(null);
      return;
    }
    if (!user?.email) {
      setError("Benutzerinformationen nicht verfügbar. Bitte neu anmelden.");
      return;
    }
    let cancelled = false;
    setWatermarkedData(null);
    setError(null);
    watermarkPdf(url, {
      name: user.name || user.email,
      email: user.email,
    })
      .then((bytes) => {
        if (!cancelled) setWatermarkedData(bytes);
      })
      .catch((e) => {
        if (!cancelled) {
          console.error("Watermark error:", e);
          const detail = e?.message || String(e);
          setError(
            `Reporting konnte nicht aufbereitet werden.\n\nDetails: ${detail}`
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, url, watermark, user?.email, user?.name]);

  const downloadFileName = (fileName && fileName.trim()) || (name && `${name}.pdf`) || "document.pdf";

  const triggerDownload = async () => {
    try {
      let bytes: Uint8Array | ArrayBuffer | null = null;
      if (watermark && watermarkedData) {
        bytes = watermarkedData;
      } else if (url) {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        bytes = await res.arrayBuffer();
      }
      if (!bytes) return;
      const blob = new Blob([bytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = downloadFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error("Download failed:", e);
    }
  };

  const canDownload = watermark ? !!watermarkedData : !!url;

  return (
    <AnimatePresence>
      {isOpen && url && (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[20000] flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-md"
            onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 20 }}
            transition={{ type: "spring", damping: 30, stiffness: 350 }}
            className="w-full md:w-[90vw] max-w-6xl h-full md:h-[90vh] bg-white rounded-none md:rounded-2xl shadow-2xl relative overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: theme.palette.background }}
          >
            {/* Desktop Header / Mobile Floating Close Container */}
            <div className="hidden md:flex justify-between items-center px-8 py-5 border-b" style={{ borderColor: theme.palette.border }}>
                <h3 className="font-extrabold text-xl truncate pr-4 uppercase tracking-tight" style={{ color: theme.palette.foreground, fontFamily: '"Nunito Sans", sans-serif' }}>
                    {name || "Dokumentenvorschau"}
                </h3>
                <div className="flex items-center gap-2">
                    <button
                        onClick={triggerDownload}
                        disabled={!canDownload}
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-black text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
                        aria-label="Download"
                    >
                        <Download size={16} color="white" />
                        Herunterladen
                    </button>
                    <button
                        onClick={onClose}
                        className="p-3 rounded-full hover:bg-gray-100 transition-all hover:rotate-90 duration-300"
                        aria-label="Close"
                    >
                        <X size={24} color="#000" />
                    </button>
                </div>
            </div>

            {/* Mobile Floating Buttons */}
            <div className="md:hidden absolute top-safe right-4 z-[1001] pt-6 flex items-center gap-2">
                <button
                    onClick={triggerDownload}
                    disabled={!canDownload}
                    className="w-12 h-12 rounded-full bg-black flex items-center justify-center shadow-xl ring-4 ring-white ring-opacity-10 disabled:opacity-40"
                    aria-label="Download"
                >
                    <Download size={20} color="white" />
                </button>
                <button
                    onClick={onClose}
                    className="w-12 h-12 rounded-full bg-black flex items-center justify-center shadow-xl ring-4 ring-white ring-opacity-10"
                    aria-label="Close"
                >
                    <X size={24} color="white" />
                </button>
            </div>

            {/* Content Container */}
            <div className="flex-1 overflow-hidden relative bg-[#F7F7F7]">
                {watermark && error ? (
                    <div className="h-full w-full flex flex-col items-center justify-center gap-4 px-6 text-center">
                        <AlertCircle size={40} color="#666" />
                        <p style={{ color: theme.palette.foreground, maxWidth: "40rem", whiteSpace: "pre-wrap", fontSize: "13px" }}>{error}</p>
                    </div>
                ) : watermark && !watermarkedData ? (
                    <div className="h-full w-full flex flex-col items-center justify-center gap-3">
                        <div className="w-10 h-10 rounded-full border-2 border-gray-300 border-t-black animate-spin" />
                        <p style={{ color: "#666", fontSize: "13px" }}>Reporting wird vorbereitet…</p>
                    </div>
                ) : (
                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                        <div className="h-full w-full">
                            <Viewer
                                enableSmoothScroll={true}
                                localization={de_DE as unknown as LocalizationMap}
                                fileUrl={watermark ? watermarkedData! : (url as string)}
                                scrollMode={ScrollMode.Vertical}
                            />
                        </div>
                    </Worker>
                )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
